import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { PauseIcon } from 'assets';
import Button from 'components/Button';
import { AIButton } from 'components/Button/aiButton';
import { FlexContainer, SubDivComponent } from 'components/Div/styles';
import ConfirmationModal from 'components/Modal/ConfirmationModal';
import TemplateManager from 'components/TemplateManager';
import { getOrderedTemplates, TemplateManagerModal } from 'components/TemplateManager/ManagerModal';
import { ErrorToast } from 'components/Toast';
import { checkIfHasActivePass } from 'components/TopBar/freeTimer';
import { STRINGS } from 'config';
import { NotificationModel } from 'pages/Consultation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import uuid from 'react-uuid';
import { startSummary } from 'store/action/summary';
import { clearTemplateInTemplateManager, getTemplates, selectTemplate } from 'store/action/templates';
import { getWallet } from 'store/action/wallet';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { Template } from 'store/reducer/templates';
import { hasUnlimitedSub } from 'store/reducer/wallet';
import { reportInfo } from 'util/datadog';
import RecordingStatusUI from './RecordingStatusUI';
import RecordingTimeWarningModal from './RecordingTimeWarningModal';
import { StyledStartIcon, TranscriptionTile } from './styles';
import Transcriptions from './Transcriptions';
import TranscriptionToggle from './TranscriptionToggle';
import { useLiveTranscription } from './useLiveTranscription';
import { useRecordingTimer } from './useRecordingTimer';

interface LiveTranscriptionProps {
  setNotification: React.Dispatch<React.SetStateAction<NotificationModel | null>>;
  notification: NotificationModel | null;
}

export enum RecordingStatus {
  LOADING = 'LOADING',
  READY = 'READY',
  ERROR = 'ERROR',
  RECORDING = 'RECORDING',
  PAUSED = 'PAUSED',
}

export enum TranscriberStatus {
  READY = 'READY',
  TRANSCRIBING = 'TRANSCRIBING',
  CLOSING = 'CLOSING',
  READY_TO_SUMMARISE = 'READY_TO_SUMMARISE',
}

const LiveTranscription: React.FC<LiveTranscriptionProps> = ({ setNotification, notification }) => {
  const {
    app: { user, selectedPractice, clinicalSystemName },
    transcription: { currentTranscription: transcription },
    summary: { consultationId: existingConsultationId },
    templates: { templates, template },
    wallet: { topup, passes, subscription },
    featureFlags,
  } = useAppState();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const userId = user.currentUserEmail!;
  const [showTranscription, setShowTranscription] = useState(false);
  const [summarise, setSummarise] = useState<boolean>(false);
  const [isRecordingTimeWarningModalOpen, setIsRecordingTimeWarningModalOpen] = useState<boolean | null>(null);
  const [hasShownRecordingWarning, setHasShownRecordingWarning] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const consultationId = useMemo(() => existingConsultationId || uuid(), [existingConsultationId]);
  const [isTemplateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [isTemplateManagerModalOpen, setTemplateManagerModalOpen] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const hasUnlimitedUse = hasUnlimitedSub(subscription);
  const selectedPracticeId = selectedPractice?.id;

  useEffect(() => {
    if (!existingConsultationId) {
      dispatch({ type: STRINGS.REDUCERS.SUMMARY.SET_CONSULATION_ID, payload: { consultationId } });
    }
  }, [dispatch, consultationId, existingConsultationId]);

  useEffect(() => {
    if (location.state?.transcript) {
      setShowTranscription(true);
      navigate('/', { replace: true, state: {} });
    }
  }, [location.state, navigate]);
  const { recordingState, interim, startRecording, stopRecording, transcriberState } = useLiveTranscription(
    consultationId,
    setNotification,
  );

  const wordCount = useCallback(
    (): number =>
      transcription
        .join(' ')
        .trim()
        .split(/\s+/)
        .filter(word => word.length > 0).length,
    [transcription],
  );

  const { formattedTime, resetTimer, secondsElapsed } = useRecordingTimer(
    recordingState === RecordingStatus.RECORDING,
    recordingState === RecordingStatus.PAUSED,
    recordingState === RecordingStatus.ERROR ? 'Recording error occurred' : null,
  );
  const formattedTimeRef = useRef(formattedTime);

  const {
    TRANSCRIBE: { MAX_WARNING_TIME_SECONDS },
  } = STRINGS;

  useEffect(() => {
    formattedTimeRef.current = formattedTime;
  }, [formattedTime]);

  useEffect(() => {
    if (
      secondsElapsed >= STRINGS.TRANSCRIBE.MAX_RECORDING_TIME_SECONDS &&
      recordingState === RecordingStatus.RECORDING
    ) {
      stopRecording(RecordingStatus.READY);
    }
  }, [secondsElapsed, recordingState, stopRecording]);

  // Stop recording if credits run out
  useEffect(() => {
    if (
      !hasUnlimitedUse &&
      topup &&
      topup.totalUsableSeconds !== null &&
      topup.totalUsableSeconds <= 0 &&
      recordingState === RecordingStatus.RECORDING
    ) {
      stopRecording(RecordingStatus.PAUSED);
    }
  }, [topup, hasUnlimitedUse, recordingState, stopRecording]);

  const startTranscribing = () => {
    reportInfo('Transcriber started', { userId, consultationId, recordingState });
    startRecording();
  };

  const startRecordingWithWarningCheck = () => {
    if (hasUnlimitedUse || (topup?.totalUsableSeconds ?? 0) >= 600) {
      return startRecording();
    }
    return startRecording();
  };

  const stopRecordingAndGetSummary = async () => {
    reportInfo('Transcriber stopped, get summary initiated', {
      userId,
      consultationId,
      timer: formattedTimeRef.current,
      wordCount: wordCount(),
    });
    await stopRecording(RecordingStatus.READY);
    setSummarise(true);
    if (selectedPracticeId) {
      await getWallet(dispatch, selectedPracticeId);
    }
  };
  const pauseRecording = useCallback(async () => {
    reportInfo('Transcriber paused', {
      userId,
      consultationId,
      timer: formattedTimeRef.current,
      wordCount: wordCount(),
    });
    await stopRecording(RecordingStatus.PAUSED);
  }, [userId, consultationId, stopRecording, wordCount]);

  useEffect(() => {
    if (
      secondsElapsed >= MAX_WARNING_TIME_SECONDS &&
      recordingState === RecordingStatus.RECORDING &&
      !hasShownRecordingWarning &&
      isRecordingTimeWarningModalOpen === null
    ) {
      setIsRecordingTimeWarningModalOpen(true);
      setHasShownRecordingWarning(true);
      pauseRecording();
    }
  }, [
    secondsElapsed,
    recordingState,
    MAX_WARNING_TIME_SECONDS,
    hasShownRecordingWarning,
    pauseRecording,
    isRecordingTimeWarningModalOpen,
  ]);

  const startTranscribingWithWarningCheck = () => {
    if (hasUnlimitedUse || (topup?.totalUsableSeconds ?? 0) >= 600) {
      return startTranscribing();
    }
    return startTranscribing();
  };

  const getSummary = async () => {
    reportInfo('Get summary initiated from pause', {
      userId,
      consultationId,
      timer: formattedTimeRef.current,
      wordCount: wordCount(),
    });
    setSummarise(true);
  };

  const handleRecordingTimeWarningContinue = () => {
    // Continue recording, modal closes automatically
    setHasShownRecordingWarning(true);
    startTranscribing();
  };

  const handleClearTranscription = () => {
    dispatch({ type: STRINGS.REDUCERS.TRANSCRIPTION.RESET_TRANSCRIPTION });
    setIsClearModalOpen(false);
  };

  const handleCancelClear = () => {
    setIsClearModalOpen(false);
  };

  const emptyTranscriptToastShownRef = useRef<boolean>(false);
  const summaryCalledRef = useRef(false);
  const isTemplateFlagOn = featureFlags.SUMMARISER_TEMPLATE_MANAGER_ENABLED;

  useEffect(() => {
    if (!summarise) {
      emptyTranscriptToastShownRef.current = false;
      summaryCalledRef.current = false;
      setHasShownRecordingWarning(false);
    }
    if (summarise && transcription.join().trim() === '' && !emptyTranscriptToastShownRef.current) {
      emptyTranscriptToastShownRef.current = true;
      toast.error(<ErrorToast message="No transcription to summarise. Check your microphone is set up correctly." />, {
        icon: false,
        autoClose: 12000,
      });
      reportInfo('No transcription to summarise', { userId, consultationId, timer: formattedTimeRef.current });
      setTimeout(() => {
        setSummarise(false);
        resetTimer();
      }, 0);
    }
    if (
      transcriberState === TranscriberStatus.READY_TO_SUMMARISE &&
      summarise &&
      transcription.join().trim() !== '' &&
      !summaryCalledRef.current
    ) {
      summaryCalledRef.current = true;
      if (selectedPracticeId) {
        startSummary(
          userId,
          consultationId,
          transcription.join(),
          dispatch,
          template,
          isTemplateFlagOn,
          selectedPracticeId,
        );
      }
      navigate('/summary', { state: { transcript: transcription } });
    }
  }, [
    transcriberState,
    summarise,
    dispatch,
    transcription,
    navigate,
    consultationId,
    resetTimer,
    userId,
    template,
    isTemplateFlagOn,
    selectedPracticeId,
  ]);

  const RecordingFlashyLight = () => (
    <RecordingStatusUI formattedTime={formattedTime} isRecording={recordingState === RecordingStatus.RECORDING} />
  );

  const toggleTranscription = () => {
    setShowTranscription(prev => !prev);
  };

  const onSelectTemplate = (template: Template) => {
    dispatch(selectTemplate(template));
    setTemplateManagerOpen(false);
  };

  const handleCloseTemplateManagerModal = () => {
    setTemplateManagerModalOpen(false);
    dispatch(clearTemplateInTemplateManager());
  };

  useEffect(() => {
    if (featureFlags.SUMMARISER_TEMPLATE_MANAGER_ENABLED && selectedPracticeId) {
      getTemplates(dispatch, selectedPracticeId, clinicalSystemName, featureFlags);
    }
  }, [dispatch, featureFlags, selectedPracticeId, clinicalSystemName]);

  const hasFreeBalanceLeft = (topup?.totalUsableSeconds ?? 0) !== 0;
  const orderedTemplates = getOrderedTemplates(templates);
  const hasActivePass = checkIfHasActivePass(passes);
  const hasAccess = hasUnlimitedUse || hasFreeBalanceLeft || hasActivePass;
  const isStartButtonDisabled =
    recordingState !== RecordingStatus.READY ||
    !hasAccess ||
    secondsElapsed >= STRINGS.TRANSCRIBE.MAX_RECORDING_TIME_SECONDS;
  const recordingButtons = {
    [RecordingStatus.READY]: (
      <>
        <Button
          id="start-consultation-button"
          icon={<StyledStartIcon />}
          iconPosition="left"
          state="primary"
          buttonText="Start"
          onClick={startRecordingWithWarningCheck}
          disabled={isStartButtonDisabled}
          style={{ padding: 0 }}
        />
        <RecordingFlashyLight />
        {featureFlags?.SUMMARISER_TEMPLATE_MANAGER_ENABLED && (
          <>
            <TemplateManager
              isOpen={isTemplateManagerOpen}
              setOpen={setTemplateManagerOpen}
              setTemplateManagerModalOpen={setTemplateManagerModalOpen}
              options={orderedTemplates}
              onSelectTemplate={onSelectTemplate}
              template={template}
            />
            <TemplateManagerModal isOpen={isTemplateManagerModalOpen} onClose={handleCloseTemplateManagerModal} />
          </>
        )}
        <AIButton
          id="generate-summary-button-ready"
          state="primary"
          buttonText="Generate"
          onClick={stopRecordingAndGetSummary}
          disabled={false} // {notification != null || recordingState !== RecordingStatus.RECORDING}
        />
        {transcription.length > 0 && (
          <Button
            id="clear-transcription-button-ready"
            state="secondary"
            buttonText="Clear"
            onClick={() => setIsClearModalOpen(true)}
            disabled={false}
            style={{ padding: '8px', minWidth: 'auto' }}
          />
        )}
      </>
    ),
    [RecordingStatus.LOADING]: <></>,
    [RecordingStatus.PAUSED]: (
      <>
        <Button
          id="resume-consultation-button"
          icon={<StyledStartIcon />}
          iconPosition="left"
          state="primary"
          buttonText="Resume"
          onClick={startTranscribingWithWarningCheck}
          disabled={!hasAccess || secondsElapsed >= STRINGS.TRANSCRIBE.MAX_RECORDING_TIME_SECONDS}
        />
        <RecordingFlashyLight />
        {featureFlags?.SUMMARISER_TEMPLATE_MANAGER_ENABLED && (
          <>
            <TemplateManager
              isOpen={isTemplateManagerOpen}
              setOpen={setTemplateManagerOpen}
              setTemplateManagerModalOpen={setTemplateManagerModalOpen}
              options={orderedTemplates}
              onSelectTemplate={onSelectTemplate}
              template={template}
            />
            <TemplateManagerModal isOpen={isTemplateManagerModalOpen} onClose={handleCloseTemplateManagerModal} />
          </>
        )}
        <AIButton
          id="generate-summary-button-paused"
          state="primary"
          buttonText="Generate"
          onClick={getSummary}
          disabled={false}
        />
        {transcription.length > 0 && (
          <Button
            id="clear-transcription-button-paused"
            state="secondary"
            buttonText="Clear"
            onClick={() => setIsClearModalOpen(true)}
            disabled={false}
            style={{ padding: '8px', minWidth: 'auto' }}
          />
        )}
      </>
    ),
    [RecordingStatus.RECORDING]: (
      <>
        <Button
          id="pause-consultation-button"
          icon={<PauseIcon />}
          iconPosition="left"
          state="primary"
          buttonText="Pause"
          onClick={pauseRecording}
          disabled={!hasAccess}
        />
        <RecordingFlashyLight />
        {featureFlags?.SUMMARISER_TEMPLATE_MANAGER_ENABLED && (
          <>
            <TemplateManager
              isOpen={isTemplateManagerOpen}
              setOpen={setTemplateManagerOpen}
              setTemplateManagerModalOpen={setTemplateManagerModalOpen}
              options={orderedTemplates}
              onSelectTemplate={onSelectTemplate}
              template={template}
            />
            <TemplateManagerModal isOpen={isTemplateManagerModalOpen} onClose={handleCloseTemplateManagerModal} />
          </>
        )}
        <AIButton
          id="generate-summary-button-recording"
          state="primary"
          buttonText="Generate"
          onClick={stopRecordingAndGetSummary}
          disabled={false}
        />
        {transcription.length > 0 && (
          <Button
            id="clear-transcription-button-recording"
            state="secondary"
            buttonText="Clear"
            onClick={() => setIsClearModalOpen(true)}
            disabled={false}
            style={{ padding: '8px', minWidth: 'auto' }}
          />
        )}
      </>
    ),
    [RecordingStatus.ERROR]: (
      <>
        <p>ERROR!</p>
      </>
    ), // todo add error message
  };

  return (
    <SubDivComponent data-automation="SubDivComponentLiveTranscription">
      <FlexContainer style={{ alignItems: 'center', gap: '8px' }}>
        {recordingButtons[recordingState]}
        <div style={{ marginLeft: 'auto' }}>
          <TranscriptionToggle showTranscription={showTranscription} onToggle={toggleTranscription} />
        </div>
      </FlexContainer>
      {showTranscription && (
        <TranscriptionTile data-automation="TranscriptionTile">
          <Transcriptions transcriptions={transcription} interim={interim} />
        </TranscriptionTile>
      )}
      <RecordingTimeWarningModal
        isOpen={Boolean(isRecordingTimeWarningModalOpen)}
        onClose={() => setIsRecordingTimeWarningModalOpen(false)}
        onContinue={handleRecordingTimeWarningContinue}
      />
      <ConfirmationModal
        isOpen={isClearModalOpen}
        title="Clear Transcription"
        message="Are you sure you want to clear the transcription? This action cannot be undone."
        confirmText="Clear"
        cancelText="Cancel"
        onConfirm={handleClearTranscription}
        onCancel={handleCancelClear}
      />
    </SubDivComponent>
  );
};

export default LiveTranscription;
